/**
 * Action item contracts for GET /action-items, GET /action-items/:id, and PUT /action-items/:id.
 * Checklist items use type "CHECK_LIST" on both list and detail payloads.
 */

export enum ActionItemType {
  CHECK_LIST = 'CHECK_LIST',
  FORM = 'FORM',
}

export enum ActionItemStatus {
  URGENT = 'URGENT',
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  DONE = 'DONE',
}

export enum ActionItemRelatedType {
  PATIENT = 'PATIENT',
  FACILITY = 'FACILITY',
}

export enum ActionItemCompletedByType {
  INTERNAL_USER = 'INTERNAL_USER',
  EXTERNAL_USER = 'EXTERNAL_USER',
}

export enum ActionItemErrorCode {
  ACTION_ITEM_ALREADY_COMPLETED = 'ACTION_ITEM_ALREADY_COMPLETED',
  NOT_FOUND = 'NOT_FOUND',
}

export interface ActionItemRelatedTo {
  fullName: string;
  type: ActionItemRelatedType;
}

export interface ActionItemChecklistProgress {
  completed: number;
  total: number;
}

export interface ActionItem {
  id: number;
  type: ActionItemType;
  status: ActionItemStatus;
  title: string;
  description: string | null;
  checklist?: ActionItemChecklistProgress;
  relatedTo: ActionItemRelatedTo[];
  createdAt: string;
}

export interface ActionItemChecklistEntry {
  id: number;
  description: string;
  isCompleted: boolean;
  completedBy: string | null;
  completedByType: ActionItemCompletedByType | null;
  completedAt: string | null;
  isCompletedByMe: boolean | null;
}

export interface ActionItemFormPayload {
  schema: string;
  inputData: string;
}

export interface ActionItemChecklistDetail {
  id: number;
  type: ActionItemType.CHECK_LIST;
  status: ActionItemStatus;
  title: string;
  description: string | null;
  checklistItems: ActionItemChecklistEntry[];
  relatedTo: ActionItemRelatedTo[];
  createdAt: string;
}

export interface ActionItemFormDetail {
  id: number;
  type: ActionItemType.FORM;
  status: ActionItemStatus;
  title: string;
  description: string | null;
  relatedTo: ActionItemRelatedTo[];
  createdAt: string;
  form: ActionItemFormPayload;
}

export type ActionItemDetail = ActionItemChecklistDetail | ActionItemFormDetail;

export interface ActionItemChecklistUpdateItem {
  id: number;
  isCompleted: boolean;
}

export interface ActionItemChecklistUpdateRequest {
  checklistItems: ActionItemChecklistUpdateItem[];
  status: ActionItemStatus;
}

export interface ActionItemFormUpdateRequest {
  form: ActionItemFormPayload;
  status: ActionItemStatus;
}

export type ActionItemUpdateRequest =
  | ActionItemChecklistUpdateRequest
  | ActionItemFormUpdateRequest;

export interface ActionItemUpdateResponse {
  message: string;
}

export interface ActionItemApiError {
  code: ActionItemErrorCode | string;
  message: string;
}

/** Postman mock GET-by-id currently returns "CHECKLIST"; treat it as CHECK_LIST. */
export function normalizeActionItemType(
  type: string | ActionItemType
): ActionItemType {
  if (type === ActionItemType.CHECK_LIST || type === 'CHECKLIST') {
    return ActionItemType.CHECK_LIST;
  }
  return type as ActionItemType;
}

export function normalizeActionItem<T extends { type: string | ActionItemType }>(
  item: T
): T {
  return {
    ...item,
    type: normalizeActionItemType(item.type),
  };
}

export function actionItemTitle(
  item: { title?: string | null; description?: string | null }
): string {
  return (item.title ?? '').trim() || (item.description ?? '').trim();
}

/** IN_PROGRESS is stored by the API but shown as PENDING in the portal. */
export function actionItemUiStatus(status: ActionItemStatus): ActionItemStatus {
  return status === ActionItemStatus.IN_PROGRESS
    ? ActionItemStatus.PENDING
    : status;
}

export function isChecklistActionItem(
  item: ActionItem | ActionItemDetail
): boolean {
  return normalizeActionItemType(item.type) === ActionItemType.CHECK_LIST;
}

export function isChecklistDetail(
  item: ActionItemDetail
): item is ActionItemChecklistDetail {
  return 'checklistItems' in item;
}

export function isFormDetail(
  item: ActionItemDetail
): item is ActionItemFormDetail {
  return 'form' in item;
}

export function isActionItemAlreadyCompletedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const httpError = error as {
    status?: number;
    error?: ActionItemApiError | string;
  };
  if (httpError.status !== 409) {
    return false;
  }
  const body = httpError.error;
  return (
    typeof body === 'object' &&
    body?.code === ActionItemErrorCode.ACTION_ITEM_ALREADY_COMPLETED
  );
}

export function parseActionItemFormInputData(
  inputData: string | undefined
): Record<string, unknown> {
  if (!inputData) {
    return {};
  }
  try {
    const parsed = JSON.parse(inputData);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    return {};
  }
  return {};
}
